import { ORDER_STATES, isClosedState, stateIndex } from "../../../services/managerApi";
import { stateLabel } from "../shared/StatusPill";

/**
 * Construit le « Verloop » d'une commande.
 *
 * Les étapes sont les états réels du flux, pas un regroupement : la commande se
 * lit dans le même vocabulaire que la liste et que le reste de l'outil.
 *
 * Sur les dates, trois qualités cohabitent et il faut les distinguer, sinon on
 * présente une prévision comme un fait :
 *
 *  - `exact`   — un horodatage réel de transition (`picked_up_at`,
 *                `processed_at`, `delivered_at`, ou `state_updated_at` pour
 *                l'étape courante). Affiché avec l'heure.
 *  - `planned` — une date planifiée (`pickup_date`, `delivery_date`). Sans
 *                heure : ce sont des dates, et leur afficher une heure produit
 *                un « 02:00 » qui n'existe pas.
 *  - aucune    — l'étape n'a pas de date connue. On n'affiche rien plutôt que
 *                de répéter « date inconnue » sur chaque ligne.
 */

/**
 * Étapes affichées.
 *
 * Les variantes « shop » (`dropped_at_shop`, `ready_for_shop`) sont écartées du
 * squelette : elles concernent le retrait en boutique, un parcours qui ne passe
 * pas par la résidence. Elles restent affichées si la commande s'y trouve.
 */
const MAIN_FLOW = [
  "confirmed",
  "picked_up",
  "dropped_at_laundry",
  "processed",
  "ready_for_delivery",
  "delivery",
];

/**
 * Meilleure date connue pour une étape.
 *
 * `state_updated_at` ne vaut que pour l'étape courante : il date le dernier
 * changement d'état, donc l'appliquer à une étape passée lui donnerait l'heure
 * d'une autre transition.
 */
const dateForState = (state, order, isCurrent) => {
  if (state === "picked_up" && order.pickedUpAt) {
    return { date: order.pickedUpAt, precision: "exact" };
  }
  if (state === "processed" && order.processedAt) {
    return { date: order.processedAt, precision: "exact" };
  }
  if (isClosedState(state) && order.deliveredAt) {
    return { date: order.deliveredAt, precision: "exact" };
  }
  if (isCurrent && order.stateUpdatedAt) {
    return { date: order.stateUpdatedAt, precision: "exact" };
  }
  if (state === "picked_up" && order.pickupDate) {
    return { date: order.pickupDate, precision: "planned" };
  }
  if (isClosedState(state) && order.deliveryDate) {
    return { date: order.deliveryDate, precision: "planned" };
  }
  return { date: null, precision: null };
};

const dateForEvent = (event) => {
  const raw = event?.occurred_at || event?.created_at || event?.at || event?.timestamp;
  if (!raw) return null;
  const date = new Date(raw);
  return Number.isNaN(date.getTime()) ? null : date;
};

/**
 * Index des horodatages réels, par état.
 *
 * Le serveur construit ses événements depuis `order_audit_logs` : la liste est
 * creuse — une commande peut n'en avoir qu'un seul — et ne dit rien des étapes
 * qu'elle ne contient pas. Elle enrichit donc le parcours, elle ne le remplace
 * pas. Le manager doit toujours voir où en est sa commande DANS le flux, pas
 * seulement ce que le journal a retenu.
 */
const datesFromEvents = (events) => {
  const byState = new Map();
  if (!Array.isArray(events)) return byState;

  events.forEach((event) => {
    const key = event?.key || event?.state || event?.action || event?.name;
    if (!ORDER_STATES.includes(key)) return;

    const date = dateForEvent(event);
    if (!date) return;

    // Le premier passage fait foi : une commande qui revient dans un état
    // garde la date de la transition d'origine.
    if (!byState.has(key)) byState.set(key, date);
  });

  return byState;
};

export const buildTimeline = (order, events, t) => {
  const currentIndex = stateIndex(order.state);
  const exactDates = datesFromEvents(events);

  // Un état hors parcours principal (retrait en boutique) est inséré à sa place
  // dans le flux plutôt qu'omis : sinon la commande n'apparaît nulle part.
  const steps = MAIN_FLOW.includes(order.state)
    ? MAIN_FLOW
    : [...MAIN_FLOW, order.state]
        .filter((state) => ORDER_STATES.includes(state))
        .sort((a, b) => stateIndex(a) - stateIndex(b));

  return steps.map((state) => {
    const index = stateIndex(state);
    let position = "future";
    if (currentIndex >= 0 && index < currentIndex) position = "done";
    if (index === currentIndex) position = "current";

    // Un horodatage du journal l'emporte sur toute déduction : c'est le seul
    // qui date la transition elle-même.
    const logged = exactDates.get(state);
    const { date, precision } = logged
      ? { date: logged, precision: "exact" }
      : dateForState(state, order, position === "current");

    return {
      key: state,
      label: stateLabel(state, t),
      date,
      precision,
      state: position,
    };
  });
};
